import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, SortOrder, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { NameDto } from '@model/NameDto'
import {
  IBRebateApprovalSortKeysDto,
  IntroducingBrokerRebateApprovalDto,
} from '@model/RebateApprovalsDto'

import { FilterQueryProps } from '@global/filter/FilterQueryModal'

import { useCallbackWithForceRefresh } from '@hooks/useCallbackWithForceRefresh'
import { IAppendableFetchResult, useFetchAppendablePage } from '@hooks/useFetch'

export const defaultApprovalsSort: { sort: string; sortOrder: SortOrder }[] = [
  { sort: 'statusPriority', sortOrder: 'ASC' },
  { sort: 'createdDate', sortOrder: 'DESC' },
]

interface IBRebateApprovals {
  query: IAppendableFetchResult<IntroducingBrokerRebateApprovalDto[]>
  tradingAccountOptions: NameDto<string>[]
  sortOptions: NameDto<IBRebateApprovalSortKeysDto | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

interface IBRebateApprovalsParams {
  onCompleted?: () => void
}

export const useIBRebateApprovals = (params: IBRebateApprovalsParams = {}): IBRebateApprovals => {
  const { onCompleted } = params
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<IBRebateApprovalSortKeysDto | null>[] = [
    {
      id: 'tradingAccountName',
      name: t('Trading Account.Trading Account'),
    },
    {
      id: 'accountType',
      name: t('Account Type'),
    },
    {
      id: 'groupName',
      name: t('IB.RebatesPage.ClientsPage.Group'),
    },
    {
      id: 'accumulatedVolumeFxm',
      name: t('IB.RebatesPage.ClientsPage.Vol. FXM'),
    },
    {
      id: 'accumulatedVolumeOther',
      name: t('IB.RebatesPage.ClientsPage.Vol. Other'),
    },
    {
      id: 'accumulatedCommission',
      name: t('Commission'),
    },
    {
      id: 'rebateAmount',
      name: t('IB.RebatesPage.GroupsPage.Rebate'),
    },
    {
      id: 'rebatePayoutStatusId',
      name: t('Status'),
    },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const { callback: rebateApprovalsCallback, forceRefresh } = useCallbackWithForceRefresh(
    async (query?: PageQuery) => {
      const res = await apiClient.getIBRebateApprovals(account?.clientIntroducingBroker?.id ?? '', {
        ...query,
        search: search,
        caller: 'ca',
        languageId: locale,
        dismissNotifications: true,
        sort: query?.sort ?? defaultApprovalsSort,
      })

      onCompleted?.()

      return res
    },
    [search, locale]
  )
  const query = useFetchAppendablePage(rebateApprovalsCallback)

  const tradingAccountOptions = useMemo(() => {
    return query.data?.map((item) => item.tradingAccount) || []
  }, [query.data])

  return {
    query,
    tradingAccountOptions,
    sortOptions,
    search,
    setSearch,
    forceRefresh,
    clearSearch,
  }
}
